import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { SectionHeader } from "../../components/SectionHeader/SectionHeader";
import { getEventBySlug, TicketType } from "../../api/events";
import getSymbolFromCurrency from "currency-symbol-map";
import LoadingPanel from "../../components/LoadingPanel/LoadingPanel";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";

interface Props {
  eventSlug: string;
  ticketTypes?: TicketType[];
  registrationOpen?: boolean;
}

export default function TicketRateTable({
  eventSlug,
  ticketTypes: ticketTypesProp,
  registrationOpen = true,
}: Props) {
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>(ticketTypesProp ?? []);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!ticketTypesProp?.length);

  useEffect(() => {
    if (ticketTypesProp?.length) {
      setTicketTypes(ticketTypesProp);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    getEventBySlug(eventSlug)
      .then((event) => {
        if (!cancelled) setTicketTypes(event.ticketTypes ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            getUserFacingErrorMessage(err, "Failed to load ticket prices."),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [eventSlug, ticketTypesProp]);

  if (isLoading) {
    return (
      <LoadingPanel
        title="Loading ticket prices"
        subtitle="Retrieving registration options."
      />
    );
  }

  if (error) {
    return <ErrorAlert>{error}</ErrorAlert>;
  }

  return (
    <div className="bg-white border border-slate-200 p-6 sm:p-8">
      <SectionHeader
        subtitle=""
        header="Registration Rates"
        description="Review ticket options below, then continue to registration and secure payment."
      />

      {ticketTypes.length === 0 ? (
        <p className="text-slate-600">Tickets are not currently available for this event.</p>
      ) : (
        <div className="overflow-x-auto mb-8">
          <table className="min-w-full border border-slate-200 text-sm">
            <thead>
              <tr className="bg-slate-100 text-left">
                <th className="p-3 font-semibold">Ticket</th>
                <th className="p-3 font-semibold">Price</th>
              </tr>
            </thead>
            <tbody>
              {ticketTypes.map((ticket) => (
                <tr key={ticket.id} className="border-t border-slate-200">
                  <td className="p-3">
                    <p className="font-medium">{ticket.name}</p>
                    {ticket.description && (
                      <p className="text-sm text-slate-500">{ticket.description}</p>
                    )}
                  </td>
                  <td className="p-3 font-semibold whitespace-nowrap">
                    {getSymbolFromCurrency(ticket.currency.toUpperCase())}
                    {(ticket.pricePence / 100).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {registrationOpen && (
        <div className="flex justify-center">
          <NavLink
            to={`/events/${eventSlug}/register`}
            className="eh-btn-primary px-10"
          >
            Register for this event
          </NavLink>
        </div>
      )}
    </div>
  );
}
